/**
 * A member's own passkeys: listing, adding, renaming and removing them.
 *
 * Adding and removing are gated by a fresh sign-in on the server: when the
 * session is not recent enough the call fails with `reauth_required`
 * (`isReauthRequired`), and the page runs the sign-in popup again before
 * retrying.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemberAuthStore } from "modules/members/auth";
import type {
  AttestationFields,
  ServerRegistrationOptions,
} from "modules/members/webauthn";
import { memberCall } from "./stepUpApi";
import {
  LOAD_PASSKEYS_FAILED_MESSAGE,
  REMOVE_PASSKEY_FAILED_MESSAGE,
  RENAME_PASSKEY_FAILED_MESSAGE,
  SAVE_PASSKEY_FAILED_MESSAGE,
  START_REGISTRATION_FAILED_MESSAGE,
  type StepUpFailure,
} from "./stepUpFailure";

/** One row of GET /members/passkeys. */
export type MFPasskey = {
  id: string;
  label: string;
  createdAt: string;
  lastUsedAt: string | null;
  /** Synced to a password manager or cloud keychain, so it survives this device. */
  backedUp: boolean;
};

/** POST /members/passkeys/registration. */
export type PasskeyRegistration = {
  challengeToken: string;
  publicKey: ServerRegistrationOptions;
};

export const PASSKEYS_KEY = ["mf", "passkeys"];

export const fetchPasskeys = (): Promise<MFPasskey[]> =>
  memberCall<MFPasskey[]>(
    "GET",
    "members/passkeys",
    undefined,
    LOAD_PASSKEYS_FAILED_MESSAGE,
  );

export const startPasskeyRegistration = (): Promise<PasskeyRegistration> =>
  memberCall<PasskeyRegistration>(
    "POST",
    "members/passkeys/registration",
    {},
    START_REGISTRATION_FAILED_MESSAGE,
  );

export const savePasskey = (
  challengeToken: string,
  attestation: AttestationFields,
  label: string,
): Promise<MFPasskey> =>
  memberCall<MFPasskey>(
    "POST",
    "members/passkeys",
    {
      challenge_token: challengeToken,
      attestation_object: attestation.attestationObject,
      client_data_json: attestation.clientDataJson,
      label,
    },
    SAVE_PASSKEY_FAILED_MESSAGE,
  );

export const renamePasskey = (id: string, label: string): Promise<MFPasskey> =>
  memberCall<MFPasskey>(
    "PATCH",
    `members/passkeys/${encodeURIComponent(id)}`,
    { label },
    RENAME_PASSKEY_FAILED_MESSAGE,
  );

export const removePasskey = (id: string): Promise<unknown> =>
  memberCall<unknown>(
    "DELETE",
    `members/passkeys/${encodeURIComponent(id)}`,
    undefined,
    REMOVE_PASSKEY_FAILED_MESSAGE,
  );

export const usePasskeys = () => {
  const accessToken = useMemberAuthStore((s) => s.accessToken);
  return useQuery<MFPasskey[], StepUpFailure>(PASSKEYS_KEY, fetchPasskeys, {
    enabled: Boolean(accessToken),
    // The page shows its own loading and error states.
    suspense: false,
    useErrorBoundary: false,
    retry: false,
  });
};

/** Marks the passkeys list stale after anything that adds, renames or removes one. */
export const useInvalidatePasskeys = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: PASSKEYS_KEY });
};

export const useRenamePasskey = () => {
  const invalidate = useInvalidatePasskeys();
  return useMutation<MFPasskey, StepUpFailure, { id: string; label: string }>(
    ({ id, label }) => renamePasskey(id, label),
    { retry: false, onSuccess: invalidate },
  );
};

export const useRemovePasskey = () => {
  const invalidate = useInvalidatePasskeys();
  return useMutation<unknown, StepUpFailure, string>((id) => removePasskey(id), {
    retry: false,
    onSuccess: invalidate,
  });
};
